// Events Page — compact list with admin-only management
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { eventsApi, usersApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import {
  Plus,
  Calendar as CalendarIcon,
  MapPin,
  Search,
  Edit,
  Archive,
  ClipboardCheck,
  RotateCcw,
  Trash2
} from 'lucide-react';

export default function EventsPage() {
  const { user, isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    event_type: 'teambuilding',
    date: new Date(),
    end_date: '',
    location: '',
    description: '',
    contact_person: '',
    contact_phone: '',
    participants_number: '',
    facilitator_ids: [],
    senior_facilitator_id: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const promises = [eventsApi.getAll(), usersApi.getAll()];
      if (isAdmin) promises.push(eventsApi.getAll({ include_archived: true }));
      const results = await Promise.all(promises);
      setEvents(results[0].data);
      setUsers(results[1].data);
      if (isAdmin && results[2]) {
        // Archived = all events with include_archived minus active events
        const activeIds = new Set(results[0].data.map(e => e.event_id));
        setArchivedEvents(results[2].data.filter(e => !activeIds.has(e.event_id)));
      }
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...formData, 
        date: formData.date.toISOString(), 
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        participants_number: formData.participants_number ? parseInt(formData.participants_number) : null 
      };
      if (editingEvent) {
        await eventsApi.update(editingEvent.event_id, payload);
        toast.success(language === 'el' ? 'Εκδήλωση ενημερώθηκε!' : 'Event updated!');
      } else {
        await eventsApi.create(payload);
        toast.success(language === 'el' ? 'Εκδήλωση δημιουργήθηκε!' : 'Event created!');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleRestore = async (eventId) => {
    if (!window.confirm(language === 'el' ? 'Επαναφορά εκδήλωσης;' : 'Restore event?')) return;
    try {
      await eventsApi.restore(eventId);
      toast.success(language === 'el' ? 'Εκδήλωση επαναφέρθηκε!' : 'Event restored!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handlePermanentDelete = async (eventId) => {
    if (!window.confirm(language === 'el' ? 'Οριστική διαγραφή; Δεν μπορεί να αναιρεθεί.' : 'Permanently delete? This cannot be undone.')) return;
    try {
      await eventsApi.permanentDelete(eventId);
      toast.success(language === 'el' ? 'Εκδήλωση διαγράφηκε οριστικά!' : 'Event permanently deleted!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleArchive = async (eventId) => {
    if (!window.confirm(
      language === 'el'
        ? 'Αρχειοθέτηση εκδήλωσης; Οι αξιολογήσεις θα διατηρηθούν.'
        : 'Archive event? Evaluations will be preserved.'
    )) return;
    try {
      await eventsApi.delete(eventId);
      toast.success(language === 'el' ? 'Εκδήλωση αρχειοθετήθηκε!' : 'Event archived!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name,
      event_type: event.event_type,
      date: new Date(event.date),
      end_date: event.end_date ? new Date(event.end_date) : '',
      location: event.location,
      description: event.description || '',
      contact_person: event.contact_person || '',
      contact_phone: event.contact_phone || '',
      participants_number: event.participants_number || '',
      facilitator_ids: event.facilitator_ids || [],
      senior_facilitator_id: event.senior_facilitator_id || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({ name: '', event_type: 'teambuilding', date: new Date(), end_date: '', location: '', description: '', contact_person: '', contact_phone: '', participants_number: '', facilitator_ids: [], senior_facilitator_id: '' });
  };

  const filteredEvents = events
    .filter(event => {
      const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           event.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === 'all' || event.event_type === typeFilter;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const seniorFacilitators = users.filter(u => u.role === 'senior_facilitator' && !u.is_archived);

  const formatEventDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy', { locale: language === 'el' ? el : enUS });
    } catch { return dateStr?.substring(0, 10); }
  };

  return (
    <Layout>
      <div className="space-y-4 animate-fade-in-up" data-testid="events-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#2A1514]">{t('events')}</h1>
            <p className="text-gray-500 mt-1 text-sm">
              {language === 'el'
                ? `${filteredEvents.length} εκδηλώσεις`
                : `${filteredEvents.length} events`}
            </p>
          </div>
          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#E6007E] hover:bg-[#b8005f]" data-testid="new-event-btn">
                  <Plus size={18} className="mr-2" />
                  {t('newEvent')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingEvent ? t('editEvent') : t('newEvent')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('eventName')}</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="event-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('eventType')}</Label>
                    <Select value={formData.event_type} onValueChange={(v) => setFormData({...formData, event_type: v})}>
                      <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="teambuilding">{t('teambuilding')}</SelectItem>
                        <SelectItem value="training">{t('training')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('eventDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="event-date-btn">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.date, 'PPP', { locale: language === 'el' ? el : enUS })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={formData.date} onSelect={(date) => date && setFormData({...formData, date})} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('location')}</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required data-testid="event-location-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{language === 'el' ? 'Υπεύθυνος Επικοινωνίας' : 'Contact Person'}</Label>
                      <Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} placeholder={language === 'el' ? 'Όνομα...' : 'Name...'} data-testid="event-contact-person-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'el' ? 'Τηλέφωνο Επικοινωνίας' : 'Contact Phone'}</Label>
                      <Input value={formData.contact_phone} onChange={(e) => setFormData({...formData, contact_phone: e.target.value})} placeholder="+30..." data-testid="event-contact-phone-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{language === 'el' ? 'Αριθμός Συμμετεχόντων' : 'Participants'}</Label>
                      <Input type="number" min="1" value={formData.participants_number} onChange={(e) => setFormData({...formData, participants_number: e.target.value})} placeholder="0" data-testid="event-participants-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('description')}</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} data-testid="event-description-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('assignSeniorFacilitator')}</Label>
                    <Select value={formData.senior_facilitator_id || undefined} onValueChange={(v) => setFormData({...formData, senior_facilitator_id: v})}>
                      <SelectTrigger><SelectValue placeholder={language === 'el' ? 'Επιλέξτε...' : 'Select...'} /></SelectTrigger>
                      <SelectContent>
                        {seniorFacilitators.length > 0 ? seniorFacilitators.map(sf => (
                          <SelectItem key={sf.user_id} value={sf.user_id}>{sf.name}</SelectItem>
                        )) : (
                          <SelectItem value="_none" disabled>{language === 'el' ? 'Δεν υπάρχουν διαθέσιμοι' : 'None available'}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
                    <Button type="submit" className="bg-[#E6007E] hover:bg-[#b8005f]" data-testid="save-event-btn">{t('save')}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input placeholder={t('search')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-9" data-testid="search-input" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px] h-9" data-testid="type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'el' ? 'Όλοι οι τύποι' : 'All types'}</SelectItem>
              <SelectItem value="teambuilding">{t('teambuilding')}</SelectItem>
              <SelectItem value="training">{t('training')}</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && archivedEvents.length > 0 && (
            <Button
              type="button"
              variant={showArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchived(!showArchived)}
              className={`h-9 text-xs whitespace-nowrap ${
                showArchived
                  ? 'bg-[#E6007E] hover:bg-[#C4006A] text-white border-transparent'
                  : 'text-gray-500 hover:text-[#E6007E] hover:border-[#E6007E]/40'
              }`}
              data-testid="filter-archived-chip"
              title={language === 'el' ? 'Εμφάνιση/Απόκρυψη αρχειοθετημένων (αυτο-αρχειοθέτηση 10+ ημέρες)' : 'Show/Hide archived (auto-archived after 10 days)'}
            >
              <Archive size={13} className="mr-1.5" />
              {language === 'el' ? 'Αρχείο' : 'Archived'}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                showArchived ? 'bg-white/20' : 'bg-gray-100 text-gray-600'
              }`}>
                {archivedEvents.length}
              </span>
            </Button>
          )}
        </div>

        {/* Compact Event List */}
        {loading ? (
          <div className="space-y-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredEvents.length > 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-0 divide-y divide-gray-100">
              {filteredEvents.map((event) => (
                <div
                  key={event.event_id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/80 transition-colors group"
                  data-testid={`event-row-${event.event_id}`}
                >
                  {/* Type indicator */}
                  <div className={`w-1.5 h-10 rounded-full flex-shrink-0 ${event.event_type === 'teambuilding' ? 'bg-[#E6007E]' : 'bg-blue-500'}`} />

                  {/* Date */}
                  <div className="w-20 flex-shrink-0 text-center">
                    <p className="text-xs font-bold text-[#2A1514] leading-tight">
                      {format(new Date(event.date), 'dd', { locale: language === 'el' ? el : enUS })}
                    </p>
                    <p className="text-[10px] text-gray-500 uppercase">
                      {format(new Date(event.date), 'MMM yy', { locale: language === 'el' ? el : enUS })}
                    </p>
                  </div>

                  {/* Name & location */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#2A1514] truncate">{event.name}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <MapPin size={10} />
                      <span className="truncate">{event.location}</span>
                    </div>
                  </div>

                  {/* Type badge */}
                  <Badge variant={event.event_type === 'teambuilding' ? 'default' : 'secondary'} className="text-[10px] px-2 py-0 h-5 flex-shrink-0">
                    {event.event_type === 'teambuilding' ? 'TB' : 'TR'}
                  </Badge>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => navigate(`/evaluations/new?event=${event.event_id}`)}
                      title={t('newEvaluation')}
                      data-testid={`evaluate-event-${event.event_id}`}
                    >
                      <ClipboardCheck size={14} className="text-[#E6007E]" />
                    </Button>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(event)} data-testid={`edit-event-${event.event_id}`}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-500 hover:text-orange-600" onClick={() => handleArchive(event.event_id)} data-testid={`archive-event-${event.event_id}`}>
                          <Archive size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <CalendarIcon size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">{t('noEvents')}</p>
              {isAdmin && (
                <Button className="mt-4 bg-[#E6007E] hover:bg-[#b8005f]" onClick={() => setDialogOpen(true)}>
                  <Plus size={18} className="mr-2" />
                  {t('newEvent')}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Archived Events Section (Admin only — toggled via filter chip above) */}
        {isAdmin && showArchived && archivedEvents.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2 px-1">
              <Archive size={14} className="text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-600">
                {language === 'el' ? 'Αρχειοθετημένες Εκδηλώσεις' : 'Archived Events'}
              </h3>
              <span className="text-[10px] text-gray-400">
                {language === 'el' ? '(αυτο-αρχειοθέτηση μετά από 10 ημέρες)' : '(auto-archived after 10 days)'}
              </span>
            </div>
            <Card className="border-0 shadow-sm bg-gray-50/50">
              <CardContent className="p-0 divide-y divide-gray-100">
                {archivedEvents.sort((a, b) => new Date(b.date) - new Date(a.date)).map((event) => (
                  <div
                    key={event.event_id}
                    className="flex items-center gap-3 px-4 py-3 opacity-70 hover:opacity-100 transition-opacity"
                    data-testid={`archived-event-${event.event_id}`}
                  >
                    <div className="w-1.5 h-10 rounded-full flex-shrink-0 bg-gray-300" />
                    <div className="w-20 flex-shrink-0 text-center">
                      <p className="text-xs font-bold text-gray-500 leading-tight">
                        {format(new Date(event.date), 'dd', { locale: language === 'el' ? el : enUS })}
                      </p>
                      <p className="text-[10px] text-gray-400 uppercase">
                        {format(new Date(event.date), 'MMM yy', { locale: language === 'el' ? el : enUS })}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-500 truncate">{event.name}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <MapPin size={10} />
                        <span className="truncate">{event.location}</span>
                      </div>
                    </div>
                    {event.auto_archived && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-amber-50 text-amber-700 border-amber-200" title={language === 'el' ? 'Αυτόματη αρχειοθέτηση (10+ ημέρες)' : 'Auto-archived (10+ days)'}>
                        AUTO
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] px-2 py-0 h-5 text-gray-400">
                      {language === 'el' ? 'Αρχείο' : 'Archived'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-green-500 hover:text-green-600"
                      onClick={() => handleRestore(event.event_id)}
                      title={language === 'el' ? 'Επαναφορά' : 'Restore'}
                        data-testid={`restore-event-${event.event_id}`}
                      >
                        <RotateCcw size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-400 hover:text-red-600"
                        onClick={() => handlePermanentDelete(event.event_id)}
                        title={language === 'el' ? 'Οριστική διαγραφή' : 'Delete permanently'}
                        data-testid={`delete-event-${event.event_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
